import { useMemo } from "react";
import { Box, Stack, styled, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import CustomPagination from "@/components/custom/custom-pagination";
import { DataTable } from "@/components/custom/data-table";
import { useAccountClosures } from "@/hooks/use-account-closures";
import { getClosureColumns } from "./_components/closure-columns";
import {
  CLOSURE_STATUS_OPTIONS,
  CLOSURE_BLOCKED_OPTIONS,
  CLOSURE_SORT_OPTIONS,
} from "../data";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  margin: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const AccountClosure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryParams = {
    page: Number(searchParams.get("page") || 0),
    size: Number(searchParams.get("size") || 10),
    search: searchParams.get("search") || "",
    status: searchParams.get("status") || undefined,
    blocked: searchParams.get("blocked") ?? undefined,
    direction: searchParams.get("direction") || "desc",
  };

  const { data: closures, isLoading, isError } = useAccountClosures(true, queryParams);

  const handleView = (row) =>
    navigate(`/approvals/closure-request/${row.id}`);

  const columns = useMemo(() => getClosureColumns(handleView), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <RootWrapper>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Account Closure Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and decide merchant account closure requests. Accounts stay
          active until a request is approved.
        </Typography>
      </Box>

      <Card>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <CustomSearchInput paramKey="search" placeholder="Search by owner or email..." />
          <CustomSelectInput
            paramKey="status"
            label="Status"
            placeholder="All Statuses"
            options={CLOSURE_STATUS_OPTIONS}
          />
          <CustomSelectInput
            paramKey="blocked"
            label="Blocked"
            placeholder="Any"
            options={CLOSURE_BLOCKED_OPTIONS}
          />
          <CustomSelectInput
            paramKey="direction"
            label="Submitted"
            // placeholder="Newest first"
            options={CLOSURE_SORT_OPTIONS}
          />
        </Stack>

        <DataTable
          data={closures?.data ?? []}
          columns={columns}
          isLoading={isLoading}
          isError={isError}
          noDataMessage="No closure requests found."
          onRowClick={handleView}
        />

        <CustomPagination
          total={closures?.totalCount ?? 0}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>
    </RootWrapper>
  );
};

export default AccountClosure;